import Konva from 'konva';
import React from 'react';
import { Group, Layer, Rect, Stage } from 'react-konva';
import { Grid } from '../../Grid';
import { Scrollbars } from '../../Scrollbars';
import { useMapCanvas } from '../contexts/MapCanvasContext';
import { useMapEditor } from '../contexts/MapEditorContext';
import {
  SELECTION_TOOL_FILL_COLOR,
  SELECTION_TOOL_STROKE_COLOR
} from '../defines';
import { SpotLineDrawing } from '../editor/spot-numbering/SpotLineDrawing';
import { useGetEditorShapesByType } from '../hooks/useGetEditorShapesByType';
import { useMapEditorHandlers } from '../hooks/useMapEditorHandlers';
import { ShapeTransformer } from '../shapes/ShapeTransformer';
import { MapCanvasPreviewShape } from './MapCanvasPreviewShape';
import { MapCanvasViewLayers } from './MapCanvasViewLayers';
import { MapCanvasEditingAreaLayer } from './layers/MapCanvasEditingAreaLayer';
import { MapCanvasGhostLayers } from './layers/MapCanvasGhostLayers';

interface MapEditorCanvasProps {
  selectedIds: string[];
  showOutside: boolean;
  stagePosition: { x: number; y: number; scale: number };
  onStageChange: (position: { x: number; y: number; scale: number }) => void;
}

const classes = {
  Wrapper: 'relative w-full h-full'
};

export const MapEditorCanvas: React.FC<MapEditorCanvasProps> = ({
  selectedIds,
  showOutside = true,
  stagePosition,
  onStageChange
}) => {
  const iconToolGhostRef = React.useRef<Konva.Group>(null);
  const stageRef = React.useRef<Konva.Stage>(null);
  const transformerRef = React.useRef<Konva.Transformer>(null);

  const {
    width,
    height,
    editingZoneWidth,
    editingZoneHeight,
    selectionBox,
    isDragging,
    gridSize
  } = useMapCanvas();

  const { showGrid } = useMapEditor();

  const {
    handleMultiDragStart,
    handleMultiDragMove,
    handleMultiDragEnd,
    isPanToolActive,
    isMouseIn,
    isDrawing,
    drawingPoints,
    cursor,
    ...handlers
  } = useMapEditorHandlers({
    iconToolGhostRef,
    stageRef,
    transformerRef
  });

  const getShapes = useGetEditorShapesByType();

  return (
    <div className={classes.Wrapper}>
      <Stage
        style={{ cursor, backgroundColor: '#F3F4F6' }}
        ref={stageRef}
        width={width}
        height={height}
        scaleX={stagePosition.scale}
        scaleY={stagePosition.scale}
        x={stagePosition.x}
        y={stagePosition.y}
        draggable={isPanToolActive}
        {...handlers}
      >
        <MapCanvasEditingAreaLayer />
        <MapCanvasViewLayers showOutside={showOutside} getShapes={getShapes} />
        <Layer>
          <Group>
            <SpotLineDrawing
              isDrawing={isDrawing}
              points={drawingPoints}
              onDrawEnd={() => {}}
            />
          </Group>
          <Group>
            {isDragging && selectionBox && (
              <Rect
                x={selectionBox.x}
                y={selectionBox.y}
                width={selectionBox.width}
                height={selectionBox.height}
                stroke={SELECTION_TOOL_STROKE_COLOR}
                strokeWidth={1}
                fill={SELECTION_TOOL_FILL_COLOR}
              />
            )}
            {selectedIds.length > 1 && (
              <ShapeTransformer ref={transformerRef} />
            )}
          </Group>
          <MapCanvasPreviewShape />
          <MapCanvasGhostLayers
            iconToolGhostRef={iconToolGhostRef}
            isMouseIn={isMouseIn}
          />
          {/*<Rect
            x={0}
            y={0}
            height={300}
            width={300}
            fill={'blue'}
            id={'editing-rect-selection'}
          />*/}
          {showGrid && <Grid />}
        </Layer>
      </Stage>
      <Scrollbars
        width={width}
        height={height}
        contentWidth={editingZoneWidth}
        contentHeight={editingZoneHeight}
        scrollX={stagePosition.x}
        scrollY={stagePosition.y}
        scale={stagePosition.scale}
        onScroll={(x, y) => onStageChange({ ...stagePosition, x, y })}
      />
    </div>
  );
};
